import { useEffect, useMemo } from 'react';
import { createContext, useContext } from 'react';

import InsightsPage from 'components/insights/InsightsPage';
import { useProfile } from 'context/profile';
import { TranslationProvider } from 'context/translations';
import Profile from 'models/Profile';
import { useInsightsActions, useInsightsStore } from 'stores/insightsStore';

type InsightsContextType = {
  profile: Profile;
};

export const InsightsContext = createContext<InsightsContextType>({
  profile: {} as Profile,
});

export const useInsightsContext = () => useContext(InsightsContext);

const InsightsContainer = () => {
  const { profile } = useProfile();
  const selectedGroupType = useInsightsStore((state) => state.selectedGroupType);
  const { setSelectedGroupType, resetFilters, setBreadcrumbs } = useInsightsActions();

  const preferredGroupType = useMemo(() => {
    if (profile?.enabledProviderTypes) {
      const { providerType } = profile?.insightsPreference?.value ?? {};

      return profile.enabledProviderTypes.find((pt) => pt.apiName === providerType) || profile.enabledProviderTypes[0];
    }
  }, [profile]);

  useEffect(() => {
    if (!selectedGroupType?.id && preferredGroupType) {
      setSelectedGroupType(preferredGroupType);
    }
  }, [preferredGroupType, selectedGroupType, setSelectedGroupType]);

  useEffect(() => {
    resetFilters();
    setBreadcrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.actingClient.id]);

  return (
    <InsightsContext value={{ profile }}>
      {selectedGroupType?.id && (
        <TranslationProvider groupType={selectedGroupType}>
          <InsightsPage />
        </TranslationProvider>
      )}
    </InsightsContext>
  );
};

export default InsightsContainer;
